import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import { useEffect, useState } from "react";
import {
  createPasskey,
  deletePasskey,
  editPasskeyName,
  fetchPasskeys,
  getCredentialCreationOptions,
} from "../SettingsService";
import { AppModal, bodyTextSx } from "global/components/AppModal";
import CustomInput from "global/components/CustomInput/CustomInput";
import strings from "global/constants/StringConstants";
import securityStyles from "./Security.styles";
import securityTokens from "./Security.tokens";
import {
  PasskeyRowIcon,
  RowDeleteIcon,
  RowEditIcon,
} from "./components/SecurityIcons";
import EmptyState from "global/components/EmptyState/EmptyState";

const styles = securityStyles;
const { table } = securityTokens;

interface CustomProps {
  loader: boolean;
  setLoader: Function;
  openAddPasskeyDialog: boolean;
  setOpenAddPasskeyDialog: Function;
}

const Passkeys = (props: CustomProps) => {
  const { loader, setLoader } = props;
  const [passKeys, setPassKeys] = useState<any[]>([]);
  const [passkeyConfigs, setPasskeyConfigs] = useState<any>({
    credentialOptions: null,
    passKeyData: [],
    openCreatePasskeyDialog: false,
    displayName: "",
    error: "",
    isEdit: false,
    isDelete: false,
  });

  useEffect(() => {
    if (props.openAddPasskeyDialog) {
      setPasskeyConfigs((prevState: any) => ({
        ...prevState,
        openCreatePasskeyDialog: true,
        isEdit: false,
        isDelete: false,
        displayName: "",
      }));
    }
  }, [props.openAddPasskeyDialog]);

  useEffect(() => {
    getPasskeys();
  }, []);

  const getPasskeys = async () => {
    try {
      setLoader(true);
      const response = await fetchPasskeys();
      setPassKeys(isTruthy(response) ? response : []);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoader(false);
    }
  };

  const handleDialogClose = () => {
    props.setOpenAddPasskeyDialog(false);
    setPasskeyConfigs((prevState: any) => ({
      ...prevState,
      displayName: "",
      openCreatePasskeyDialog: false,
      error: "",
      isEdit: false,
      isDelete: false,
    }));
  };

  const openPasskeyDialog = (
    passkey: any,
    mode: { isEdit: boolean; isDelete: boolean },
  ) => {
    setPasskeyConfigs((prevState: any) => ({
      ...prevState,
      passKeyData: passkey,
      openCreatePasskeyDialog: true,
      displayName: passkey.displayName,
      error: "",
      ...mode,
    }));
  };

  const getEmptyState = () => (
    <EmptyState
      size="medium"
      icon="key"
      title="No passkeys yet"
      body="Create one to sign in faster on this device."
    />
  );

  const getSkeletonRows = () =>
    [...Array(table.skeletonRows)].map((_, index) => (
      <Box
        key={index}
        sx={styles.tableRow(
          table.passkeyColumns,
          index === table.skeletonRows - 1,
        )}
      >
        <Skeleton variant="text" width="45%" sx={styles.tableSkeletonCell} />
        <Skeleton variant="text" width="70%" sx={styles.tableSkeletonCell} />
        <Box sx={styles.actionCell}>
          <Skeleton
            variant="rounded"
            width={securityTokens.rowAction.size}
            height={securityTokens.rowAction.size}
          />
        </Box>
      </Box>
    ));

  const getPasskeyRow = (passkey: any, index: number) => (
    <Box
      key={`${passkey.displayName}-${index}`}
      sx={styles.tableRow(table.passkeyColumns, index === passKeys.length - 1)}
    >
      <Box sx={styles.deviceCell}>
        <PasskeyRowIcon
          sx={styles.deviceIcon}
          color={table.deviceIconColor}
          strokeWidth={table.deviceIconStrokeWidth}
        />
        <Box component="span" sx={styles.deviceName}>
          {passkey.displayName}
        </Box>
      </Box>
      <Box component="span" sx={styles.mutedCell}>
        {passkey.username}
      </Box>
      <Box sx={styles.actionCell}>
        <Tooltip title="Rename passkey" placement="top" arrow>
          <IconButton
            aria-label={`Rename passkey ${passkey.displayName}`}
            onClick={() =>
              openPasskeyDialog(passkey, { isEdit: true, isDelete: false })
            }
            sx={styles.rowIconButton(false)}
          >
            <RowEditIcon
              sx={styles.rowIconGlyph}
              strokeWidth={securityTokens.rowAction.iconStrokeWidth}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete passkey" placement="top" arrow>
          <IconButton
            aria-label={`Delete passkey ${passkey.displayName}`}
            onClick={() =>
              openPasskeyDialog(passkey, { isEdit: false, isDelete: true })
            }
            sx={styles.rowIconButton(true)}
          >
            <RowDeleteIcon
              sx={styles.rowIconGlyph}
              strokeWidth={securityTokens.rowAction.iconStrokeWidth}
            />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  const getPasskeyCard = () => (
    <Box sx={styles.splitCard}>
      <Box sx={styles.splitCardHeader}>
        <Box sx={styles.splitCardHeaderText}>
          <Typography component="h2" sx={styles.cardTitle}>
            Passkeys
          </Typography>
          <Typography component="div" sx={styles.cardDescription}>
            Sign in with your fingerprint, face, or device PIN instead of a
            password.
          </Typography>
        </Box>
        <Button
          id="create_passkey_btn"
          variant="contained"
          disabled={loader}
          onClick={() => props.setOpenAddPasskeyDialog(true)}
          sx={styles.compactPrimaryButton}
        >
          Create passkey
        </Button>
      </Box>

      {loader || passKeys.length > 0 ? (
        <>
          <Box sx={styles.tableHeader(table.passkeyColumns)}>
            <Box component="span">PASSKEY</Box>
            <Box component="span">USER</Box>
            <Box component="span" sx={styles.tableHeaderActionCell}>
              ACTION
            </Box>
          </Box>
          {loader ? getSkeletonRows() : passKeys.map(getPasskeyRow)}
        </>
      ) : (
        getEmptyState()
      )}
    </Box>
  );

  const getDialogTitleContent = () => {
    if (passkeyConfigs.isEdit) {
      return "Update";
    } else if (passkeyConfigs.isDelete) {
      return "Delete";
    } else {
      return "Create";
    }
  };

  // "<Action> Passkey" is now the modal's own title, so the body carries only
  // the field or the delete confirmation.
  const getDialogBody = () => {
    return (
      <Stack direction="column">
        <>
          {!passkeyConfigs.isDelete ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <CustomInput
                  id="passkey_name"
                  name="addPasskeyName"
                  propsToInputElement={{
                    maxLength: strings.TAGS_LIMIT,
                    "aria-label": "Passkey name",
                  }}
                  placeHolder="Enter passkey name"
                  onChange={(event: any) =>
                    setPasskeyConfigs((prevState: any) => ({
                      ...prevState,
                      error: event.target.value ? "" : prevState.error,
                      displayName: event.target.value,
                    }))
                  }
                  value={passkeyConfigs.displayName}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <span style={{ fontSize: "0.75rem" }}>
                          {passkeyConfigs?.displayName.length} / 25
                        </span>
                      </InputAdornment>
                    ),
                  }}
                  error={
                    !isTruthy(passkeyConfigs.displayName) &&
                    passkeyConfigs.error
                  }
                  helperText={
                    !isTruthy(passkeyConfigs.displayName)
                      ? passkeyConfigs.error
                      : ""
                  }
                />
              </Grid>
            </Grid>
          ) : (
            <Typography sx={bodyTextSx}>
              {`${"Are you sure you want to delete "}`}
              <span style={{ fontWeight: 700 }}>
                {passkeyConfigs.displayName} passkey ?
              </span>
            </Typography>
          )}
        </>
      </Stack>
    );
  };

  const handleUpdatePasskey = async () => {
    try {
      if (isTruthy(passkeyConfigs.passKeyData)) {
        setLoader(true);
        const response = await editPasskeyName({
          ...passkeyConfigs.passKeyData,
          displayName: passkeyConfigs.displayName,
        });
        handleDialogClose();
        openSuccessNotification(response.message);
        await getPasskeys();
      }
    } catch (error: any) {
      setLoader(false);
      handleErrorMessage(error);
    } finally {
      setLoader(false);
    }
  };

  const handleCreatePasskey = async () => {
    try {
      if (handleValidation() && isTruthy(passkeyConfigs.displayName)) {
        setLoader(true);
        const options = await getCredentialCreationOptions(
          passkeyConfigs.displayName,
        );
        if (!options || options instanceof Error) {
          throw new Error("Failed to retrieve credential creation options");
        }
        setPasskeyConfigs((prevState: any) => ({
          ...prevState,
          credentialOptions: options,
          openCreatePasskeyDialog: false,
        }));
        const response = await createPasskey(options);
        openSuccessNotification(response.message);
        getPasskeys();
      }
    } catch (error: any) {
      setLoader(false);
      if (
        error.message ===
        "The user attempted to register an authenticator that contains one of the credentials already registered with the relying party."
      ) {
        openErrorNotification(
          "You have already added this device. Please use a different device and try again.",
        );
      } else {
        handleErrorMessage(error);
      }
    } finally {
      setLoader(false);
    }
  };

  const handleDeletePasskey = async () => {
    try {
      setLoader(true);
      const response = await deletePasskey(passkeyConfigs.passKeyData);
      handleDialogClose();
      openSuccessNotification(response.message);
      setLoader(false);
    } catch (error: any) {
      setLoader(false);
      handleErrorMessage(error);
    } finally {
      setLoader(false);
    }
  };

  const handleValidation = () => {
    if (!passkeyConfigs.displayName) {
      setPasskeyConfigs((prevState: any) => ({
        ...prevState,
        error: "Please enter passkey name",
      }));
      return false;
    }
    return true;
  };

  const getPasskeyActionFunction = async () => {
    if (!handleValidation()) {
      return;
    }

    if (passkeyConfigs.isEdit) {
      await handleUpdatePasskey();
    } else if (passkeyConfigs.isDelete) {
      await handleDeletePasskey();
    } else {
      await handleCreatePasskey();
    }
  };

  const handlePrimary = async () => {
    await getPasskeyActionFunction();
    if (handleValidation()) {
      handleDialogClose();
      await getPasskeys();
    }
  };

  return (
    <>
      {getPasskeyCard()}
      <AppModal
        open={passkeyConfigs.openCreatePasskeyDialog}
        onClose={() => handleDialogClose()}
        title={`${getDialogTitleContent()} Passkey`}
        tone={passkeyConfigs.isDelete ? "destructive" : "default"}
        size="sm"
        busy={loader}
        // Passkey registration hands off to the browser's WebAuthn prompt, so
        // the scrim may not pull the shell out from under it. Esc stays live —
        // `busy` already locks it for the duration of that hand-off.
        disableBackdropClose
        primaryLabel={getDialogTitleContent()}
        onPrimary={handlePrimary}
        primaryLoading={loader}
        cancelLabel="Cancel"
        onCancel={() => handleDialogClose()}
      >
        {getDialogBody()}
      </AppModal>
    </>
  );
};

export default Passkeys;
